/** @format */

/* eslint react-hooks/rules-of-hooks: "warn", @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, { Ref, forwardRef } from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import CloseIcon from '@toucan/client/global/icons/close.svg';

import { Link, LinkSize } from '@toucan/client/shared/components/link';

import { SearchFilterMenuOptionList } from '@toucan/client/features/search/components/option-list';
import { SearchFilterMenuPriceModifierOptions } from '@toucan/client/features/search/components/price-modifier-options';
import { SearchFilterMenuPriceOptions } from '@toucan/client/features/search/components/price-options';
import { PriceFilterOption } from '@toucan/client/features/search/components/price-options/constants';

import { styles } from './search-filter-menu.styles';
import { SearchFilterMenuProps } from './types';

const DivWithRef = (props: { children: any }, ref: Ref<HTMLElement>) => {
    return React.createElement('div', { ref, ...props });
};

export const DEFAULT_TEST_ID = 'search-filter-menu';

const blacklistedProps = ['atScrollEnd', 'filter'];
const StyledSearchFilterMenu = styled(forwardRef(DivWithRef)).withConfig({
    shouldForwardProp: (prop) =>
        isPropValid(prop) && !blacklistedProps.includes(prop),
})`
    ${styles}
`;

const SearchFilterMenuWithRef = (
    {
        'data-testid': dataTestId = DEFAULT_TEST_ID,
        selectedOptions = [],
        displayOptionQuantity = true,
        isLoading = false,
        filter,
        applyLabel,
        cancelLabel,
        clearLabel,
        onSelect,
        onApply,
        onCancel,
        onClear,
        onSelectVariant,
        selectedVariant,
    }: SearchFilterMenuProps,
    ref: Ref<HTMLElement>
) => {
    const { t } = useTranslation();

    const numberSelected = selectedOptions.length;
    const shouldRenderHeader = filter.id !== SearchFacet.productPriceModifier;

    function renderFilterOptions() {
        if (filter.id === SearchFacet.productPrice) {
            return (
                <SearchFilterMenuPriceOptions
                    filter={filter}
                    selectedOptions={selectedOptions}
                    onSelect={onSelect}
                    onClear={onClear}
                    displayOptionQuantity={displayOptionQuantity}
                    isLoading={isLoading}
                    selectedVariant={selectedVariant as PriceFilterOption}
                    onSelectVariant={(variant) => onSelectVariant?.(variant)}
                />
            );
        }

        if (filter.id === SearchFacet.productPriceModifier) {
            return (
                <SearchFilterMenuPriceModifierOptions
                    filter={filter}
                    selectedOptions={selectedOptions}
                    onSelect={onSelect}
                    onClear={onClear}
                    isLoading={isLoading}
                    selectedCount={numberSelected}
                />
            );
        }

        return (
            <SearchFilterMenuOptionList
                filter={filter}
                selectedOptions={selectedOptions}
                onSelect={onSelect}
                displayOptionQuantity={displayOptionQuantity}
                isLoading={isLoading}
            />
        );
    }

    return (
        <StyledSearchFilterMenu data-testid={dataTestId} ref={ref}>
            <Icon
                title={cancelLabel ?? ''}
                aria-label={cancelLabel ?? ''}
                onClick={onCancel}
                variant={IconVariant.NAVIGATION}
                size={IconSize.S}
                data-testid="filter-close-button"
                className="cancel"
            >
                <CloseIcon />
            </Icon>
            {shouldRenderHeader && (
                <div className="header">
                    {filter?.label && (
                        <Heading size={HeadingSize.H4}>{filter.label}</Heading>
                    )}
                    <span className="selected-count">
                        {t('{{count}} selected', { count: numberSelected })}
                    </span>
                    <Link
                        size={LinkSize.SMALL}
                        onClick={onClear}
                        className="clear"
                        data-testid={`${dataTestId}__clear-button`}
                    >
                        {clearLabel}
                    </Link>
                </div>
            )}
            {renderFilterOptions()}
            <div className="footer">
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    onClick={onCancel}
                    data-testid={`${dataTestId}__cancel-button`}
                >
                    {cancelLabel}
                </Button>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.SMALL}
                    onClick={onApply}
                    data-testid={`${dataTestId}__apply-button`}
                >
                    {applyLabel}
                </Button>
            </div>
        </StyledSearchFilterMenu>
    );
};

export const SearchFilterMenu = forwardRef(SearchFilterMenuWithRef);
